import { useState, type FormEvent } from "react"

import { ExclamationTriangleIcon, LinkIcon } from "@heroicons/react/20/solid"
import { parseAuroraDesignRef } from "@logic/lib/aurora"

import type {
  SolarAuroraLinkEquipmentDto,
  SolarAuroraLinkPreviewDto,
  SolarProposalAuroraDto,
} from "~/api/dto/solar-proposal-aurora.dto"
import useLinkSolarAuroraDesign from "~/api/hooks/useLinkSolarAuroraDesign"
import useSolarAuroraLinkPreview from "~/api/hooks/useSolarAuroraLinkPreview"
import { solarProposalErrorMessages } from "~/api/solar-proposal-errors"
import { parseAxiosError } from "~/utils/parseAxiosError"

export const AURORA_DESIGN_REF_INVALID = `Enter an Aurora CAD link or a design ID.`
export const AURORA_SOURCE_LOCK_CONFIRMATION = `Aurora becomes this proposal's design source. Vision design versions become read-only and the designer is no longer available. This can't be undone.`
export const AURORA_ADDRESS_MISMATCH = `The Aurora project's address doesn't match the proposal's. Check that this is the right design before linking.`

const NOT_SET = `—`

const sectionTitleClass = `text-xs font-semibold tracking-wide text-muted uppercase`
const secondaryButtonClass = `inline-flex items-center justify-center gap-1.5 rounded-md border border-control-border px-4 py-2 text-sm font-semibold text-foreground hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50`
const primaryButtonClass = `inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50`

const formatEquipment = ({
  name,
  quantity,
}: SolarAuroraLinkEquipmentDto): string => `${quantity} × ${name}`

const ErrorMessages = ({
  error,
  fallback,
}: {
  error: unknown
  fallback: string
}): JSX.Element => {
  const messages = solarProposalErrorMessages(error)
  return (
    <div
      role="alert"
      className="space-y-1 rounded-xl border border-danger/40 bg-danger/10 p-4 text-sm text-foreground"
    >
      {messages.length > 0 ? (
        messages.map((message) => <p key={message}>{message}</p>)
      ) : (
        <p>{parseAxiosError(error, fallback)}</p>
      )}
    </div>
  )
}

const PreviewRow = ({
  label,
  value,
}: {
  label: string
  value: string
}): JSX.Element => (
  <div className="flex justify-between gap-4 py-3">
    <dt className="text-muted">{label}</dt>
    <dd className="text-right font-semibold break-words text-foreground">
      {value}
    </dd>
  </div>
)

const Preview = ({
  preview,
}: {
  preview: SolarAuroraLinkPreviewDto
}): JSX.Element => {
  const equipment = [preview.module, ...preview.inverters].filter(
    (item): item is SolarAuroraLinkEquipmentDto => item !== null,
  )

  return (
    <div className="flex flex-col gap-4" data-testid="solar-aurora-link-preview">
      <dl className="divide-y divide-border rounded-xl border border-border px-4 text-sm">
        <PreviewRow label="Name" value={preview.name} />
        <PreviewRow
          label="System size"
          value={`${preview.systemSizeKw.toFixed(2)} kW`}
        />
        <PreviewRow
          label="Annual production"
          value={`${Math.round(preview.annualProductionKwh).toLocaleString(`en-US`)} kWh`}
        />
        <PreviewRow
          label="Aurora offset"
          value={
            preview.annualOffsetPercent == null
              ? NOT_SET
              : `${Math.round(preview.annualOffsetPercent)}%`
          }
        />
      </dl>
      <section aria-label="Equipment" className="flex flex-col gap-2">
        <h4 className={sectionTitleClass}>Equipment</h4>
        {equipment.length > 0 ? (
          <ul className="space-y-1 text-sm text-foreground">
            {equipment.map((item) => (
              <li key={item.id}>{formatEquipment(item)}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted">No Powur equipment matched.</p>
        )}
        {preview.unmappedEquipment.length > 0 ? (
          <p className="text-sm text-muted">
            Not matched to Powur equipment:{` `}
            {preview.unmappedEquipment.join(`, `)}
          </p>
        ) : null}
      </section>
      <section aria-label="Addresses" className="flex flex-col gap-2">
        <h4 className={sectionTitleClass}>Addresses</h4>
        <dl
          className={`divide-y rounded-xl border px-4 text-sm ${
            preview.addressesMatch
              ? `divide-border border-border`
              : `divide-warning/30 border-warning/60 bg-warning/10`
          }`}
          data-testid="solar-aurora-link-addresses"
        >
          <PreviewRow
            label="Aurora project"
            value={preview.auroraProjectAddress ?? NOT_SET}
          />
          <PreviewRow
            label="Proposal"
            value={preview.proposalAddress ?? NOT_SET}
          />
        </dl>
        {preview.addressesMatch ? null : (
          <p
            role="status"
            className="flex items-start gap-2 text-sm text-foreground"
          >
            <ExclamationTriangleIcon
              aria-hidden="true"
              className="mt-0.5 size-4 shrink-0 text-warning"
            />
            {AURORA_ADDRESS_MISMATCH}
          </p>
        )}
      </section>
      {preview.linkable ? null : (
        <div
          role="alert"
          aria-label="Why this design can't be linked"
          className="space-y-1 rounded-xl border border-danger/40 bg-danger/10 p-4 text-sm text-foreground"
        >
          {preview.reasons.map((reason) => (
            <p key={reason}>{reason}</p>
          ))}
        </div>
      )}
    </div>
  )
}

const LinkForm = ({
  proposalId,
  onCancel,
  onLinked,
}: {
  proposalId: string
  onCancel: () => void
  onLinked: (saved: SolarProposalAuroraDto) => void
}): JSX.Element => {
  const [input, setInput] = useState(``)
  const [attempted, setAttempted] = useState(false)
  // The ref the shown preview is for; editing the input drops it.
  const [previewRef, setPreviewRef] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const preview = useSolarAuroraLinkPreview(proposalId, previewRef)
  const link = useLinkSolarAuroraDesign({ onLinked })

  const parsed = parseAuroraDesignRef(input)
  const invalid = attempted && parsed === null

  const submitPreview = (event: FormEvent): void => {
    event.preventDefault()
    setAttempted(true)
    if (!parsed) return
    const ref = input.trim()
    if (ref === previewRef) void preview.refetch()
    else setPreviewRef(ref)
  }

  const linkable =
    previewRef !== null &&
    preview.data?.linkable === true &&
    // A failed re-preview keeps the last answer; it no longer stands.
    !preview.isError &&
    !preview.isFetching

  return (
    <div className="flex flex-col gap-4">
      <form className="flex flex-col gap-2" onSubmit={submitPreview} noValidate>
        <label
          htmlFor="solar-aurora-link-ref"
          className="text-sm font-semibold text-foreground"
        >
          Aurora design link or ID
        </label>
        <div className="flex flex-wrap gap-3">
          <input
            id="solar-aurora-link-ref"
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={input}
            aria-invalid={invalid}
            aria-describedby={
              invalid ? `solar-aurora-link-ref-error` : undefined
            }
            placeholder="https://v2.aurorasolar.com/projects/…/designs/…/cad"
            className="min-w-0 flex-1 rounded-md border border-control-border bg-background px-3 py-2 text-sm text-foreground aria-invalid:border-danger"
            disabled={link.isLoading}
            onChange={(event) => {
              setInput(event.target.value)
              setPreviewRef(null)
              setConfirming(false)
            }}
          />
          <button
            type="submit"
            className={secondaryButtonClass}
            disabled={preview.isFetching || link.isLoading}
          >
            {preview.isFetching ? `Previewing…` : `Preview`}
          </button>
        </div>
        {invalid ? (
          <p id="solar-aurora-link-ref-error" className="text-sm text-danger">
            {AURORA_DESIGN_REF_INVALID}
          </p>
        ) : null}
      </form>
      {previewRef !== null && preview.isError ? (
        <ErrorMessages
          error={preview.error}
          fallback="The Aurora design could not be previewed."
        />
      ) : null}
      {previewRef !== null && preview.data && !preview.isError ? (
        <Preview preview={preview.data} />
      ) : null}
      {link.isError ? (
        <ErrorMessages
          error={link.error}
          fallback="The Aurora design could not be linked."
        />
      ) : null}
      {link.isSuccess ? (
        // The step becomes the Aurora drawer once the design read refetches.
        <p role="status" className="text-sm text-muted">
          Linked. Loading the Aurora design…
        </p>
      ) : confirming && linkable ? (
        <div
          aria-live="polite"
          className="flex flex-col gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4 text-sm text-foreground"
        >
          <p className="font-semibold">Link {preview.data?.name}?</p>
          <p>{AURORA_SOURCE_LOCK_CONFIRMATION}</p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className={secondaryButtonClass}
              disabled={link.isLoading}
              onClick={() => setConfirming(false)}
            >
              Back
            </button>
            <button
              type="button"
              className={primaryButtonClass}
              disabled={link.isLoading}
              onClick={() => {
                if (previewRef !== null) {
                  link.mutate({ proposalId, ref: previewRef })
                }
              }}
            >
              {link.isLoading ? `Linking…` : `Link and use Aurora`}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end gap-3">
          <button
            type="button"
            className={secondaryButtonClass}
            disabled={link.isLoading}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className={primaryButtonClass}
            disabled={!linkable || link.isLoading}
            onClick={() => setConfirming(true)}
          >
            Link
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * An administrator's first Aurora link for a Solar Proposal V2 with no Aurora
 * design: paste a CAD link or design ID, preview what it brings in (and every
 * reason it can't be linked now), confirm the Source Lock, and link. The
 * System Design step shows it only when the Aurora read's
 * `viewerCanLinkAurora` allows; the gateway enforces every rule again.
 */
export const SolarAuroraLink = ({
  proposalId,
  onLinked,
}: {
  proposalId: string
  onLinked: (saved: SolarProposalAuroraDto) => void
}): JSX.Element => {
  const [open, setOpen] = useState(false)

  if (!open) {
    return (
      <div>
        <button
          type="button"
          className={secondaryButtonClass}
          onClick={() => setOpen(true)}
        >
          <LinkIcon aria-hidden="true" className="size-4" />
          Link Aurora design
        </button>
      </div>
    )
  }

  return (
    <section
      aria-label="Link Aurora design"
      className="flex flex-col gap-4 rounded-xl border border-border p-4"
      data-testid="solar-aurora-link"
    >
      <h3 className={sectionTitleClass}>Link Aurora design</h3>
      <LinkForm
        proposalId={proposalId}
        onCancel={() => setOpen(false)}
        onLinked={onLinked}
      />
    </section>
  )
}
